import type { CreateBookingResponse } from '../api/types';

interface GtagWindow {
  gtag?: (...args: unknown[]) => void;
  dataLayer?: unknown[];
}

export interface PurchaseEvent {
  transaction_id: string;
  value: number;
  currency: string;
  affiliation?: string;
  items: { item_id: string; item_name: string; price: number; quantity: number }[];
}

export function purchaseFromBooking(result: CreateBookingResponse, currency: string): PurchaseEvent | null {
  const tx = result.ga_transaction;
  if (!tx?.id) return null;
  const item = result.ga_item;
  return {
    transaction_id: String(tx.id),
    value: typeof tx.revenue === 'number' ? tx.revenue : 0,
    currency,
    affiliation: tx.affilation ?? undefined,
    items: item?.id
      ? [{ item_id: String(item.id), item_name: item.name ?? 'Service', price: item.price ?? 0, quantity: item.quantity ?? 1 }]
      : [],
  };
}

/**
 * Fires a GA4 `purchase`. Uses gtag when present, else pushes to the dataLayer (GTM), else no-op.
 * Resolves when gtag reports the event sent, or after `timeoutMs` — so a redirect never loses it.
 */
export function trackPurchase(event: PurchaseEvent, win: GtagWindow = window as GtagWindow, timeoutMs = 1000): Promise<'gtag' | 'dataLayer' | 'none'> {
  if (typeof win.gtag === 'function') {
    return new Promise((resolve) => {
      const done = () => resolve('gtag');
      const timer = setTimeout(done, timeoutMs);
      win.gtag!('event', 'purchase', {
        ...event,
        event_callback: () => {
          clearTimeout(timer);
          done();
        },
        event_timeout: timeoutMs,
      });
    });
  }
  if (Array.isArray(win.dataLayer)) {
    win.dataLayer.push({ ecommerce: null });
    win.dataLayer.push({ event: 'purchase', ecommerce: event });
    return Promise.resolve('dataLayer');
  }
  return Promise.resolve('none');
}
